import * as React from "react";
import { useCommonHooks } from "../../../hooks";
import { useLanguages } from "../../../hooks/applicationHooks/useLanguages";
import { ActionCode, CancellationReasonEditDto } from "../../../api";
import { useCrudApi } from "../../../hooks/useCrudApi";
import {
  FGTextInput,
  FlexFieldGroup,
  showSuccess,
  useFGFormik,
} from "nsitools-react";
import * as Yup from "yup";
import {
  CardFieldGroup,
  CustomFormGenerator,
  FGTranslations,
} from "../../../components";

export interface ICancellationReasonDetailPanelProps {
  cancellationReasonId: number;
}

export const CancellationReasonDetailPanel: React.FunctionComponent<
  ICancellationReasonDetailPanelProps
> = ({ cancellationReasonId }) => {
  const { apis, t, hasAction, validators } = useCommonHooks();
  const { buildDefaultDataTranslations } = useLanguages();

  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditCancellationReason]),
    [hasAction]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: ["objective-question-details", cancellationReasonId],
      fetchFn: async () => {
        if (cancellationReasonId > 0) {
          return apis.cancellationReasons.get(cancellationReasonId);
        }
        const newItem = CancellationReasonEditDto.fromJS({});
        newItem.dataTranslation = await buildDefaultDataTranslations();

        return newItem;
      },
      saveFn: async (toSave: CancellationReasonEditDto) => {
        return apis.cancellationReasons.save(toSave);
      },
      onSaved: () => {
        showSuccess(t("CancellationReasonSaved"));
        refetch();
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string().required(t("Required")).max(150),
        dataTranslation: validators.dataTranslationValidator,
      }),
    [t, validators.dataTranslationValidator]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  return (
    <FlexFieldGroup columns={2}>
      <CardFieldGroup title={t("Details")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <FGTextInput name="code" label={t("Code")} forceCase="upper" />
          <FGTranslations label={t("Text")} />
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default CancellationReasonDetailPanel;
